/**
 * CommandPalette Component
 *
 * Global search command palette (⌘K / Ctrl+K)
 * Searches across flows, primitives, executions, devices, locations, and schedules.
 */

import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Puzzle,
  PlayCircle,
  Server,
  Cpu,
  MapPin,
  Calendar,
  Loader2,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useGlobalSearch, SearchResultType } from '@/hooks/useGlobalSearch';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Icon mapping for result types
const typeIcons: Record<SearchResultType, React.ComponentType<{ className?: string }>> = {
  flow: FileText,
  primitive: Puzzle,
  execution: PlayCircle,
  device: Server,
  modem: Cpu,
  location: MapPin,
  schedule: Calendar,
};

// Labels for result types
const typeLabels: Record<SearchResultType, string> = {
  flow: 'Flows',
  primitive: 'Primitives',
  execution: 'Executions',
  device: 'Edge Devices',
  modem: 'Modems',
  location: 'Locations',
  schedule: 'Schedules',
};

// Status badge colors
const getStatusColor = (status?: string): string => {
  switch (status) {
    case 'online':
    case 'active':
    case 'completed':
    case 'published':
      return 'text-status-success';
    case 'offline':
    case 'failed':
    case 'error':
      return 'text-accent-red';
    case 'running':
    case 'pending':
    case 'queued':
      return 'text-accent-blue';
    case 'draft':
    case 'paused':
      return 'text-muted-foreground';
    default:
      return 'text-muted-foreground';
  }
};

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { query, setQuery, resultsByType, loading, totalCount } = useGlobalSearch();

  // Handle keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  // Handle result selection
  const handleSelect = useCallback((url: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(url);
  }, [navigate, onOpenChange, setQuery]);

  // Clear query when closing
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open, setQuery]);

  // Get result groups with items
  const resultGroups = Object.entries(resultsByType)
    .filter(([_, results]) => results.length > 0)
    .map(([type, results]) => ({
      type: type as SearchResultType,
      label: typeLabels[type as SearchResultType],
      results,
    }));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search flows, modems, executions..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && query && totalCount === 0 && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {!loading && !query && (
          <CommandEmpty>
            <div className="text-center">
              <p className="text-muted-foreground">Start typing to search...</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Search flows, primitives, executions, devices, and more
              </p>
            </div>
          </CommandEmpty>
        )}

        {!loading && resultGroups.map((group, index) => (
          <React.Fragment key={group.type}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={group.label}>
              {group.results.map((result) => {
                const Icon = typeIcons[result.type];
                return (
                  <CommandItem
                    key={`${result.type}-${result.id}`}
                    value={`${result.type}-${result.id}-${result.title}`}
                    onSelect={() => handleSelect(result.url)}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 overflow-hidden">
                      <span className="truncate">{result.title}</span>
                      {result.subtitle && (
                        <span className="ml-2 text-xs text-muted-foreground truncate">
                          {result.subtitle}
                        </span>
                      )}
                    </div>
                    {result.status && (
                      <span className={`text-xs ${getStatusColor(result.status)}`}>
                        {result.status}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}

        {!loading && query && totalCount > 0 && (
          <>
            <CommandSeparator />
            <div className="px-4 py-2 text-xs text-muted-foreground text-center">
              {totalCount} result{totalCount !== 1 ? 's' : ''} found
            </div>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
